<template>
  <div class="auth">
    <p v-if="!step" class="page-loading">{{ t('page-loading') }}</p>

    <template v-if="step === 'failed' || step === 'expired'">
      <NavBar />
      <p class="warn">{{ step === 'failed' ? failText : t('wechat-expired') }}</p>
      <p><button type="button" @click="retry">{{ t('button-wechat-sign-in') }}</button></p>
    </template>

    <template v-if="step === 'welcome' || step === 'name' || step === 'link'">
      <div class="brand">
        <img :alt="t('app-name')" src="@/assets/slowgo.png">
        <h2>{{ t('app-name') }}</h2>
      </div>

      <template v-if="step === 'welcome'">
        <button type="button" class="wechat-main" @click="useWeChat">{{ t('button-wechat-sign-in') }}</button>
        <p><a href="#" class="have-account" @click.prevent="step = 'link'">{{ t('wechat-have-account') }}</a></p>
      </template>

      <form v-if="step === 'name'" novalidate @submit.prevent="create">
        <label>{{ t('wechat-your-name') }}<input v-model="name" name="displayName" /></label>
        <p v-if="error" class="warn">{{ error }}</p>
        <button type="submit" :disabled="busy">{{ t('button-wechat-confirm-name') }}</button>
      </form>

      <form v-if="step === 'link'" novalidate @submit.prevent="link">
        <p class="info">{{ t('wechat-link-hint') }}</p>
        <label>{{ t('field-email') }}<input v-model="email" type="email" name="email" autocomplete="username" /></label>
        <label>{{ t('field-password') }}<input v-model="password" type="password" name="password" autocomplete="current-password" /></label>
        <p v-if="error" class="warn">{{ error }}</p>
        <button type="submit" :disabled="busy">{{ t('button-sign-in') }}</button>
      </form>
    </template>
  </div>
</template>

<script>
// WeChat sends the browser back here with ?code&state (docs/plans/2026-10-08-wechat-sign-in.md §5.2).
// A WeChat slowGo hasn't seen gets the welcome page (docs/plans/2026-10-09-wechat-welcome-page.md):
// 'welcome' (one button; the tap asks WeChat for the nickname), then 'name'; or 'link' (email).
import NavBar from '@/components/NavBar';
import api from '@/api/api';
import auth from '@/auth';
import { t, errorText } from '@/texts';
import { authorize, saved, save, forget } from './wechat';

export default {
  name: 'WeChatReturnPage',
  components: { NavBar },
  data() {
    return {
      step: null,   // null (working), 'welcome', 'name', 'link', 'failed', 'expired'
      name: '', email: '', password: '',
      error: null, busy: false, failText: '',
    };
  },
  async created() {
    const s = saved();
    const { code, state } = this.$route.query;
    // The code works once: drop it from the address, so a reload doesn't send it again.
    window.history.replaceState(window.history.state, '', '/wechat/return');

    if (s.link) return this.linkThenGo(s, code, state);
    if (!state || state !== s.state) return this.fail();
    if (!code) {
      // Declined WeChat's popup: the name page, without the nickname.
      if (s.pending && s.askedNickname) return this.show('name');
      return this.fail();
    }
    try {
      const a = await api.call('POST', 'wechat/authorizations', { code });
      if (a.sessionId) return this.signedIn(a.sessionId, a.userId);
      save({ pending: a.id });
      if (s.askedNickname) {
        this.name = a.nickname || '';
        this.show('name');
      } else {
        this.show('welcome');
      }
    } catch (e) {
      this.fail(e.msg);
    }
  },
  methods: {
    t,
    // A disabled account gets its own message; any other failure the WeChat one.
    fail(code) {
      this.failText = code === 'account-disabled' ? t('error-account-disabled') : t('error-wechat-failed');
      this.step = 'failed';
    },
    show(step) {
      this.error = null;
      this.step = step;
    },
    // Linking a signed-in account (wechat.js, linkSignedIn): whatever happens, the page it was on
    // opens next; a failure only leaves the account unlinked.
    async linkThenGo(s, code, state) {
      if (code && state === s.state) {
        try {
          await api.call('POST', 'wechat/authorizations', { code, link: true });
        } catch (e) {
          // stays unlinked
        }
      }
      forget();
      this.$router.replace(s.next || { name: 'list' });
    },
    async signedIn(sessionId, userId) {
      await auth.signedIn(sessionId, userId);
      const next = saved().next;
      forget();
      this.$router.replace(next || { name: 'list' });
    },
    retry() {
      const next = saved().next;
      forget();
      authorize('snsapi_base', next);
    },
    // From a tap, so WeChat shows its popup asking to allow the nickname.
    useWeChat() {
      save({ askedNickname: true });
      authorize('snsapi_userinfo');
    },
    problem(e) {
      const code = e.msg || String(e);
      if (code === 'expired' || code === 'already-done') this.step = 'expired';
      else this.error = errorText(code);
    },
    async create() {
      this.error = null;
      this.busy = true;
      try {
        const r = await api.call('POST', 'wechat/identities',
          { authorization: saved().pending, user: { displayName: this.name } });
        await this.signedIn(r.sessionId, r.userId);
      } catch (e) {
        this.problem(e);
      } finally {
        this.busy = false;
      }
    },
    async link() {
      this.error = null;
      this.busy = true;
      try {
        await auth.signIn(this.email, this.password);
        const s = this.$store.state;
        await api.call('POST', 'wechat/identities',
          { authorization: saved().pending, user: { id: s.user.id } });
        await this.signedIn(s.sessionId, s.user.id);
      } catch (e) {
        this.problem(e);
      } finally {
        this.busy = false;
      }
    },
  },
};
</script>

<style src="@/components/auth.css"></style>
<style scoped>
.page-loading {
  margin-top: 30vh;
  color: #888;
}

.brand {
  margin-top: 40px;
}

.brand img {
  width: 160px;
  height: 160px;
}

.brand h2 {
  margin: 8px 0 24px;
}

.auth button.wechat-main {
  width: 260px;
  height: 52px;
  font-size: 20px;
  color: #fff;
  background: #07c160;
  border: none;
  border-radius: 6px;
}

.have-account {
  font-size: 13px;
  color: #576b95;
}
</style>
